// Pure Saudi gold pricing helpers. SAR is pegged to USD at 3.75.

export const SAR_PER_USD = 3.75;
export const GRAMS_PER_OZ = 31.1035;
export const GRAMS_PER_TOLA = 11.6638;

// Local Saudi jeweller premium (SAR per gram, 24K). Small, transparent.
export const LOCAL_PREMIUM_SAR_PER_G = 2.0;

export const SA_KARATS = [24, 22, 21, 18, 14] as const;
export type SaKarat = (typeof SA_KARATS)[number];

export type SaCity = {
  slug: string;
  ar: string;
  blurb: string; // 80-120 word Arabic context
  premium: number; // SAR/g over base
};

export const SA_CITY_DATA: SaCity[] = [
  {
    slug: "riyadh",
    ar: "الرياض",
    premium: 0,
    blurb:
      "الرياض هي العاصمة والمرجع الرئيسي لأسعار الذهب في المملكة العربية السعودية، وتضم أسواق الذهب الكبرى مثل سوق الذهب في الديرة وسوق طيبة وأسواق العليا. أسعار الذهب في الرياض تمثل القاعدة التي تبنى عليها أسعار باقي المدن السعودية، وتتأثر مباشرة بسعر السبائك العالمي مع إضافة هامش بسيط للصاغة. التحديث اللحظي يعكس أسعار البيع والشراء عند كبار تجار الذهب في الرياض مع احتساب أجرة الصياغة بشكل منفصل.",
  },
  {
    slug: "jeddah",
    ar: "جدة",
    premium: 0.5,
    blurb:
      "جدة عروس البحر الأحمر تحتل المرتبة الثانية في حجم تجارة الذهب بالمملكة، وتتميز بسوق الذهب الشهير في البلد وسوق قابل وسوق الصواريخ. تتأثر أسعار الذهب في جدة بحركة المسافرين والحجاج والمعتمرين القادمين من جميع أنحاء العالم، مما يجعل الطلب أكثر تنوعاً. هامش الصاغة في جدة عادة ما يكون قريباً جداً من الرياض مع فروقات بسيطة تعكس تكاليف المحلات في وسط البلد.",
  },
  {
    slug: "mecca",
    ar: "مكة المكرمة",
    premium: 0.6,
    blurb:
      "مكة المكرمة تشهد طلباً موسمياً ضخماً على الذهب خاصة خلال موسم الحج والعمرة، حيث يقتني الحجاج الذهب كهدايا وذكرى من الأراضي المقدسة. أسواق الذهب الرئيسية في مكة تتمركز حول الحرم المكي الشريف وفي أسواق المسعى وأجياد. أسعار الذهب في مكة قد ترتفع قليلاً عن الرياض بسبب ارتفاع الإيجارات في المنطقة المركزية، لكن الجودة والمصداقية مضمونة من خلال هيئات الرقابة على المعادن الثمينة.",
  },
  {
    slug: "medina",
    ar: "المدينة المنورة",
    premium: 0.6,
    blurb:
      "المدينة المنورة تجمع بين الطابع التراثي والتجاري في أسواق الذهب، مع تركز المحلات حول المسجد النبوي الشريف وفي أسواق المناخة والمدينة. الذهب في المدينة المنورة يحظى بطلب عال من زوار المسجد النبوي ومن المقيمين على حد سواء. أسعار الذهب في المدينة المنورة تتبع تقريباً نفس مستوى مكة مع فروقات طفيفة، وتعتمد المحلات على تحديث الأسعار يومياً وفق سعر السبائك العالمي وسعر صرف الريال السعودي.",
  },
  {
    slug: "dammam",
    ar: "الدمام",
    premium: 0.4,
    blurb:
      "الدمام عاصمة المنطقة الشرقية وأحد أبرز مراكز تجارة الذهب في الخليج، ويضم سوق الذهب في الدمام محلات وصاغات معروفة على مستوى المملكة. تتأثر أسعار الذهب في الدمام بقربها من البحرين والإمارات، حيث يقارن المشترون الأسعار في الأسواق الخليجية المجاورة. هامش الصاغة في الدمام تنافسي بسبب كثرة المحلات والقرب من موانئ الاستيراد، مما يجعل الدمام خياراً ممتازاً لشراء الذهب الاستثماري والمشغولات الذهبية.",
  },
  {
    slug: "tabuk",
    ar: "تبوك",
    premium: 0.7,
    blurb:
      "تبوك مدينة الشمال السعودي تشهد نمواً متزايداً في تجارة الذهب مع توسع المشاريع التنموية مثل نيوم والبحر الأحمر. أسواق الذهب في تبوك أصغر حجماً من الرياض وجدة لكنها تخدم احتياجات سكان منطقة تبوك بشكل كامل. الأسعار في تبوك قد تكون أعلى قليلاً بسبب تكاليف النقل من الرياض ومحدودية عدد المحلات، لكن جودة الذهب ومعايير الدمغة الحكومية مضمونة بنفس درجة المدن الكبرى.",
  },
  {
    slug: "khobar",
    ar: "الخبر",
    premium: 0.4,
    blurb:
      "الخبر مدينة المنطقة الشرقية النابضة بالحياة، تضم العديد من محلات الذهب الفاخرة في المولات الكبرى مثل الراشد مول وأسواق الذهب التقليدية في وسط المدينة. الخبر معروفة بتنوع تصاميم المجوهرات وقربها من البحرين، حيث تستفيد من المنافسة الإقليمية لتقديم أسعار وخدمات تنافسية. أسعار الذهب في الخبر تتبع تقريباً نفس مستوى الدمام مع فروقات بسيطة تعكس تكاليف العقارات في المناطق التجارية الراقية.",
  },
  {
    slug: "najran",
    ar: "نجران",
    premium: 0.8,
    blurb:
      "نجران مدينة الجنوب السعودي الحدودية مع اليمن تتميز بطابع فريد في تجارة الذهب، حيث تتأثر التصاميم بالتراث المحلي والخليجي. أسواق الذهب في نجران أصغر نسبياً لكنها تخدم سكان المنطقة الجنوبية الكبيرة. الأسعار في نجران قد تكون أعلى من الرياض بحوالي 0.8 ريال للجرام بسبب تكاليف النقل والتوزيع، لكن الجودة مضمونة من خلال الرقابة الحكومية على معادن الذهب والفضة.",
  },
  {
    slug: "abha",
    ar: "أبها",
    premium: 0.7,
    blurb:
      "أبها عاصمة منطقة عسير ومدينة الضباب السعودية، تشهد نشاطاً سياحياً متزايداً مما انعكس على تجارة الذهب فيها. أسواق الذهب في أبها تتمركز في وسط المدينة وفي المولات الجديدة مثل أبها مول. تصاميم الذهب في أبها تجمع بين الطابع التراثي العسيري والتصاميم العصرية، مع أسعار قريبة من المتوسط السعودي مع إضافة هامش بسيط يعكس تكاليف النقل من المدن الرئيسية كالرياض وجدة.",
  },
];

export type SaKaratPrices = Record<SaKarat, number>;

export function computeSaPrices(args: {
  spotUsdPerOz: number;
  cityPremium?: number;
}): SaKaratPrices {
  const safe = Number.isFinite(args.spotUsdPerOz) ? args.spotUsdPerOz : 0;
  const base24 =
    (safe * SAR_PER_USD) / GRAMS_PER_OZ + LOCAL_PREMIUM_SAR_PER_G + (args.cityPremium ?? 0);
  return {
    24: base24,
    22: base24 * (22 / 24),
    21: base24 * (21 / 24),
    18: base24 * (18 / 24),
    14: base24 * (14 / 24),
  };
}

export function formatSar(n: number, dp = 2): string {
  const safe = Number.isFinite(n) ? n : 0;
  return safe.toLocaleString("en-SA", { minimumFractionDigits: dp, maximumFractionDigits: dp }) + " ر.س";
}

export function formatSarInt(n: number): string {
  const safe = Number.isFinite(n) ? n : 0;
  return Math.round(safe).toLocaleString("en-SA") + " ر.س";
}

// 20-question Arabic FAQ for Saudi gold rate page
export const SA_FAQS: { q: string; a: string }[] = [
  {
    q: "كم سعر جرام الذهب عيار 21 اليوم في السعودية؟",
    a: "يتم تحديث سعر جرام الذهب عيار 21 في السعودية كل 15 دقيقة من سعر السبائك العالمي بعد تحويله إلى الريال السعودي بسعر صرف 3.75 ريال للدولار. السعر اللحظي ظاهر في الجدول أعلى الصفحة.",
  },
  {
    q: "ما الفرق بين عيار 18 و 21 و 22 و 24 في الذهب؟",
    a: "عيار 24 هو الذهب الخالص بنسبة 99.9% ويستخدم للسبائك والاستثمار، عيار 22 (916) هو الأكثر استخداماً للمشغولات وفيه 91.6% ذهب، عيار 21 (875) شائع في الخليج بنسبة 87.5%، وعيار 18 (750) فيه 75% ذهب ويناسب المجوهرات المرصعة بالألماس.",
  },
  {
    q: "متى أفضل وقت لشراء الذهب في السعودية؟",
    a: "أفضل وقت لشراء الذهب يكون عند انخفاض السعر العالمي وارتفاع الدولار مقابل العملات الأخرى. في السعودية، يُنصح بالشراء في منتصف الأسبوع وتجنب الأعياد والمناسبات حيث يرتفع الطلب وأجرة الصياغة.",
  },
  {
    q: "كيف يتم حساب سعر الذهب اليومي في السعودية؟",
    a: "يتم حساب السعر بمعادلة: (سعر الأونصة بالدولار × 3.75 ريال) ÷ 31.1035 جرام + هامش الصاغة. والنتيجة هي سعر جرام الذهب عيار 24، ثم يتم احتساب باقي الأعيار بنسبة نقاوة كل عيار.",
  },
  {
    q: "هل أسعار الذهب اليوم في الرياض وجدة متساوية؟",
    a: "السعر الأساسي للذهب موحد في جميع مدن المملكة لأنه مرتبط بسعر السبائك العالمي. الفروقات البسيطة تعود إلى تكاليف المحلات وأجرة الصياغة التي تختلف من مدينة إلى أخرى بمقدار 0.3 إلى 0.8 ريال للجرام.",
  },
  {
    q: "كم نصاب زكاة الذهب في السعودية؟",
    a: "نصاب زكاة الذهب هو 85 جراماً من الذهب الخالص (عيار 24) حسب الراجح، أو 87.48 جراماً عند بعض العلماء. إذا ملكت هذا المقدار لمدة سنة هجرية كاملة، يجب عليك إخراج 2.5% زكاة. استخدم الحاسبة في الأسفل لمعرفة قيمة النصاب اليوم بالريال.",
  },
  {
    q: "ما هي ضريبة القيمة المضافة على الذهب في السعودية؟",
    a: "تطبق ضريبة القيمة المضافة 15% على المشغولات الذهبية والمجوهرات في السعودية. أما السبائك والمسكوكات الذهبية الاستثمارية بعيار 24 فهي معفاة من ضريبة القيمة المضافة وفقاً للائحة هيئة الزكاة والضريبة والجمارك.",
  },
  {
    q: "كيف أعرف الذهب الأصلي من المغشوش في السعودية؟",
    a: "ابحث عن الدمغة الحكومية للذهب على القطعة (916 لعيار 22، أو 750 لعيار 18، أو 999 لعيار 24). اطلب فاتورة رسمية من المحل تتضمن الوزن والعيار وسعر الجرام. يمكنك أيضاً اختبار الذهب في مختبر معتمد من الهيئة السعودية للمواصفات.",
  },
  {
    q: "هل يجوز شراء الذهب بالتقسيط في السعودية؟",
    a: "شراء الذهب الخالص (عيار 24) يجب أن يكون نقداً ويداً بيد حسب أحكام الصرف في الشريعة. أما المشغولات الذهبية فيختلف فيها العلماء؛ بعضهم يجيز التقسيط لأنها صناعة لا ذهب خالص، والأحوط الشراء النقدي.",
  },
  {
    q: "ما هي توقعات أسعار الذهب في السعودية لعام 2026؟",
    a: "تشير التحليلات إلى استمرار اتجاه الذهب التصاعدي في 2026 مدفوعاً بمشتريات البنوك المركزية وتخفيضات الفائدة الأمريكية والتوترات الجيوسياسية. التوقعات تشير إلى نطاق سعري بين 4000 و 4500 دولار للأونصة، وهو ما يعني استمرار ارتفاع السعر بالريال السعودي.",
  },
  {
    q: "ما هي أفضل محلات الذهب في الرياض؟",
    a: "من أبرز محلات الذهب في الرياض: ديوان الذهب، دار السبائك، صرافة الخليج، الراجحي للمجوهرات، ولاؤلؤة الخليج. جميعها تتبع نفس السعر الأساسي مع فروقات في أجرة الصياغة وتصاميم المجوهرات.",
  },
  {
    q: "كم سعر كيلو الذهب اليوم في السعودية؟",
    a: "سعر كيلو الذهب يعادل 1000 ضعف سعر جرام الذهب عيار 24. استخدم الحاسبة في الأسفل لمعرفة السعر الدقيق اللحظي بالريال السعودي. الكيلو يستخدم عادة للسبائك الاستثمارية الكبيرة.",
  },
  {
    q: "هل ارتفاع الدولار يؤثر على أسعار الذهب في السعودية؟",
    a: "نعم بشكل مباشر، لأن الريال السعودي مربوط بالدولار الأمريكي بسعر صرف ثابت 3.75. عندما يرتفع الذهب عالمياً بالدولار، يرتفع بالريال بنفس النسبة تقريباً. الانخفاض كذلك. لا توجد مخاطر صرف بين الدولار والريال.",
  },
  {
    q: "ما الفرق بين سعر البيع وسعر الشراء للذهب؟",
    a: "سعر البيع هو السعر الذي يبيع به التاجر للعميل، وسعر الشراء هو السعر الذي يدفعه التاجر عند شرائه الذهب من العميل (الذهب المستعمل). الفارق بينهما يسمى الهامش ويتراوح عادة بين 3 إلى 10 ريال للجرام.",
  },
  {
    q: "هل يمكنني بيع ذهبي القديم في السعودية بسعر اليوم؟",
    a: "نعم، معظم محلات الذهب في السعودية تشتري الذهب المستعمل بسعر اليوم بعد خصم هامش بسيط (3-10 ريال للجرام). يفضل التوجه لعدة محلات والمقارنة قبل البيع. الذهب الكسر يقدر حسب وزنه الفعلي وعياره بعد فحصه.",
  },
  {
    q: "ما تأثير أسعار النفط على الذهب في السعودية؟",
    a: "ارتفاع أسعار النفط يقوي عادة الإيرادات السعودية ويزيد الإنفاق الحكومي، مما قد يدعم الطلب على الذهب محلياً. كما أن الذهب والنفط يتحركان أحياناً في نفس الاتجاه كأصول حقيقية في فترات التضخم العالمي.",
  },
  {
    q: "هل أسعار الذهب اليوم في ارتفاع أم انخفاض؟",
    a: "اتجاه أسعار الذهب اليومي يظهر في قسم الاتجاه أعلى الصفحة بسهم واضح ↑ أو ↓ مع النسبة المئوية للتغير. يتم تحديث الاتجاه لحظياً من حركة السوق العالمية وتغيرات سعر صرف الدولار.",
  },
  {
    q: "كيف أقرأ الرسم البياني لأسعار الذهب؟",
    a: "الرسم البياني يعرض سعر الذهب عبر الزمن. المحور الأفقي هو التاريخ والعمودي هو السعر بالريال. يمكنك التبديل بين فترات مختلفة (يوم، أسبوع، شهر، سنة، 5 سنوات، 10 سنوات) لرؤية الاتجاه طويل المدى.",
  },
  {
    q: "هل الذهب استثمار جيد في السعودية في 2026؟",
    a: "الذهب يعتبر من أفضل أدوات حفظ القيمة وحماية الثروة من التضخم. ينصح الخبراء الماليون بتخصيص 10-15% من المحفظة الاستثمارية للذهب المادي كتنويع وحماية. السعودية توفر بيئة مثالية للاستثمار في الذهب مع غياب ضريبة على السبائك الاستثمارية.",
  },
  {
    q: "ما هي مصادر بيانات أسعار الذهب في هذه الصفحة؟",
    a: "يتم استخراج سعر السبائك العالمي (XAU/USD) من مصادر السوق المعتمدة وتحويله إلى الريال السعودي عبر سعر الربط الثابت 3.75 ريال للدولار، مع إضافة هامش الصاغة المحلي. التحديث يتم كل 15 دقيقة تلقائياً.",
  },
];